<template>
  <div v-if="isWaiting">
    <font-awesome-icon
      icon="spinner"
      class="tw-animate-spin" />
  </div>
  <div v-if="isError">
    <font-awesome-icon
      class="tw-text-status-red tw-text-3xl tw-mr-2 tw-align-middle"
      icon="fa-solid fa-triangle-exclamation" />
    <span
      class="tw-font-semibold tw-text-lg"
      v-html="t(errorText)"></span>
  </div>
  <div v-else>
    <div v-if="screen == 'edit'">
      <div>
        <span
          class="tw-font-semibold tw-px-4"
          v-html="t('modals.modal_device_add_label_name')"></span>
        :
        <span class="tw-font-semibold tw-text-lg">
          {{ rcResponse?.deviceName }}
        </span>
      </div>
      <div>
        <div
          v-for="(item, idx) in rcResponse?.values"
          :key="item.name"
          :class="[
            'tw-p-4 tw-shadow-lg hover:tw-bg-gray-100 tw-rounded-sm',
            idx % 2 == 0 ? 'tw-bg-white' : 'tw-bg-gray-100',
            areFieldsValid[idx] ? '' : 'tw-border-red-500 tw-border-2 tw-border-solid',
          ]">
          <RemoteControlInput
            :key="item.index + '_' + item.subIndex"
            :data="item"
            @update:value="handleHistory"
            @is-valid="(val: boolean) => (areFieldsValid[idx] = val)"
            :value="findValueInHistory(item.index, item.subIndex)" />
        </div>
      </div>

      <div class="tw-w-full tw-text-right">
        <BaseButton
          class="tw-mt-4"
          variant="neutral"
          :text="t('generic.reset')"
          @click="handleReset"
          :disabled="history.length === 0"
          event-type="button"></BaseButton>
        <BaseButton
          class="tw-mt-4"
          variant="neutral"
          :text="t('button_save')"
          @click="screen = 'confirmation'"
          :disabled="areFieldsValid.includes(false) || history.length === 0"
          event-type="button"></BaseButton>
      </div>
    </div>
    <div v-else-if="screen == 'confirmation'">
      <RemoteControlHistorySummary
        :history="history"
        :status="historyStatus"
        :deviceId="rcResponse?.deviceId || 0"
        @delete-history="handleDeleteHistory" />
      <div class="tw-border tw-border-solid tw-border-status-red-500 tw-p-4 tw-flex tw-flex-col tw-items-end tw-justify-center">
        <div class="tw-text-center tw-self-center">
          <h3
            class="tw-font-semibold tw-text-status-red-500 tw-text-center tw-py-4"
            v-html="t('remote_control.warning')"></h3>
          <br />
          <p
            class="tw-font-medium"
            v-html="t('remote_control.warning-text')"></p>
          <ol class="tw-text-start">
            <li
              v-for="i in 3"
              :key="i"
              class="tw-p-1"
              v-html="t('remote_control.warning-text-' + i)"></li>
          </ol>
        </div>

        <div class="tw-flex tw-flex-row tw-items-center tw-justify-center">
          <input
            type="checkbox"
            v-model="consent"
            id="rc-consent"
            class="tw-scale-150 tw-mx-2" />
          <label
            for="rc-consent"
            class="tw-font-semibold tw-p-4"
            v-html="t('remote_control.warning-accept')"></label>
        </div>
      </div>
      <div class="tw-w-full tw-flex tw-justify-end">
        <div class="tw-inline-block">
          <OTPInput
            @update="(val: string) => (otp = val)"
            @submit="handleSubmit"
            :resetCounter="otpReset" />
        </div>
      </div>
      <div class="tw-w-full tw-text-right">
        <BaseButton
          class="tw-mt-4"
          variant="neutral"
          :text="t('button_cancel')"
          @click="screen = 'edit'"
          event-type="button"></BaseButton>
        <BaseButton
          class="tw-mt-4"
          variant="neutral"
          :text="t('generic.submit')"
          @click="handleSubmit"
          :disabled="!otp || isSubmissionPending || !consent"
          :is-waiting="isSubmissionPending"
          event-type="button"></BaseButton>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { RCResponse } from "@/api/RemoteControlConnector";
import BaseButton from "@/components/library/BaseButton/BaseButton.vue";
import OTPInput from "@/components/library/OTPInput/OTPInput.vue";
import { onMounted, Ref, ref } from "vue";
import RemoteControlHistorySummary from "./RemoteControlHistorySummary.vue";
import RemoteControlInput from "./RemoteControlInput.vue";
import { usePendingValues } from "@/utils/devices/composables/usePendingValues";
import { useTranslation } from "i18next-vue";
import { useRemoteControlStore } from "@/storeModules/remoteControl/remoteControlStore";
import { useUISettingsStore } from "@/storeModules/uisettings/UISettingsStore";
import { useGlobalConfigsStore } from "@/storeModules/globalConfigs/globalConfigs";

const { t } = useTranslation();
const { pollPendingValues } = usePendingValues();

const remoteControlStore = useRemoteControlStore();
const uiSettingsStore = useUISettingsStore();
const { connector } = useGlobalConfigsStore();
const rcResponse: Ref<RCResponse | undefined> = ref();
const isWaiting: Ref<boolean> = ref(true);
const isError: Ref<boolean> = ref(false);
const errorText: Ref<string> = ref("");
const areFieldsValid = ref<boolean[]>([]);
const screen = ref<"edit" | "confirmation">("edit");
const otp = ref("");
const otpReset = ref(0);
const isSubmissionPending = ref(false);
const consent = ref(false);
const history = ref<RCHistoryValue[]>([]);
const historyStatus = ref<{
  [key: string]: "OK" | string;
}>({});

const findValueInHistory = (index: number, subIndex: number) => {
  const value = history.value.find((h) => h.index + "_" + h.subIndex === index + "_" + subIndex)?.value;

  if (value === undefined || value === null || isNaN(value)) {
    return rcResponse.value?.values.find((h) => h.index + "_" + h.subIndex === index + "_" + subIndex)?.currentSetPoint;
  }
  return value;
};

const handleReset = () => {
  history.value = [];
};
const handleHistory = (val: RCHistoryValue) => {
  const key = val.index + "_" + val.subIndex;
  if (isNaN(val.value)) {
    return;
  }

  const existingVal = history.value.find((item) => item.index + "_" + item.subIndex === key);
  if (existingVal) {
    if (existingVal.oldValue == val.value) {
      history.value = history.value.filter((item) => item.index + "_" + item.subIndex !== key);
    } else {
      existingVal.value = val.value;
    }
  } else {
    history.value.push(val);
  }
};

const handleDeleteHistory = (key: string) => {
  const newHistory = history.value.filter((item) => item.index + "_" + item.subIndex !== key);
  if (newHistory.length === 0) {
    screen.value = "edit";
  }
  history.value = newHistory;
};

const getRCValuesOutOfHistory = () => {
  if (!rcResponse.value || !rcResponse.value.deviceId) {
    return [];
  }
  const values = history.value.map((item) => {
    return {
      deviceId: rcResponse.value!.deviceId,
      index: item.index,
      subIndex: item.subIndex,
      value: item.value,
    };
  });
  return values;
};

const handleSubmit = async () => {
  if (history.value.length === 0 || !otp.value) {
    return;
  }
  isSubmissionPending.value = true;
  try {
    if (!rcResponse.value) {
      return;
    }
    const response = await connector?.remoteControl.setRCValue(getRCValuesOutOfHistory(), otp.value);
    if (typeof response === "string") {
      uiSettingsStore.setError({
        message: t(response, { defaultValue: t("remote_control.history.error") }),
        type: "error",
      });
      return;
    }
    historyStatus.value = response as {
      [key: string]: "OK" | string;
    };
    if (
      Object.values(
        response as {
          [key: string]: "OK" | string;
        }
      ).every((item) => item === "OK")
    ) {
      await pollPendingValues(rcResponse.value.deviceId, 3000);

      setTimeout(() => {
        handleReset();
        screen.value = "edit";
        otp.value = "";
      }, 1000 * history.value.length);
    }
  } catch (error) {
    console.error("error", error);
    uiSettingsStore.setError({
      message: t(error as string, { defaultValue: t("remote_control.history.error") }),
      type: "error",
    });
  } finally {
    isSubmissionPending.value = false;
    otp.value = "";
    otpReset.value += 1;
  }
};

onMounted(async () => {
  try {
    const response: RCResponse | undefined = await connector?.remoteControl.getRCInfo(
      remoteControlStore.targetDeviceID,
      remoteControlStore.targetSPPIdx,
      remoteControlStore.targetSPPSubIdx
    );

    if (!response || !response.values) {
      isError.value = true;
      errorText.value = "platform.error_no_values_found";
      return;
    }
    rcResponse.value = response as RCResponse;

    areFieldsValid.value = new Array(rcResponse.value.values.length).fill(true);
  } catch (error) {
    console.error(error);
    isError.value = true;
    errorText.value = error as string;
  } finally {
    isWaiting.value = false;
  }
});
</script>
<script lang="ts">
export type RCHistoryValue = {
  name: string;
  value: number;
  index: number;
  subIndex: number;
  oldValue: number;
  unit: string;
};
</script>
